<template>
  <div class="aset">
    <section class="sui-card">
      <div class="sui-card__head">
        <h2 class="sui-card__title">입금 계좌</h2>
      </div>
      <div class="sui-card__body">
        <p class="aset__lead">
          구매 기관이 결제 안내 화면에서 보는 계좌입니다. 통장이 바뀌면 여기서 바로 고쳐 주세요.
        </p>

        <p v-if="errorMessage" class="aset__error">
          <AppIcon icon="mdi-alert-outline" :size="16" />
          {{ errorMessage }}
        </p>

        <form class="aset__form" data-tour="admin-settings-form" @submit.prevent="onSave">
          <label class="aset__field">
            <span>은행명</span>
            <input v-model.trim="form.bankName" type="text" maxlength="50" placeholder="예: 국민은행" />
          </label>
          <label class="aset__field">
            <span>계좌번호</span>
            <input
              v-model.trim="form.account"
              type="text"
              maxlength="50"
              placeholder="예: 123456-78-901234"
            />
          </label>
          <label class="aset__field">
            <span>예금주</span>
            <input v-model.trim="form.holder" type="text" maxlength="50" placeholder="예: 주식회사 함우리" />
          </label>
          <label class="aset__field aset__field--wide">
            <span>안내 문구</span>
            <textarea
              v-model.trim="form.notice"
              rows="3"
              maxlength="255"
              placeholder="필요하면 입금·확인 관련 안내를 적어 주세요. 비우면 안내가 숨겨집니다."
            />
          </label>
          <label class="aset__field aset__field--wide">
            <span>몰 홈 운영 공지</span>
            <textarea
              v-model.trim="form.opsNotice"
              rows="2"
              maxlength="255"
              placeholder="예: 설 연휴 배송 일정 안내 — 비우면 배너가 숨겨집니다."
            />
          </label>

          <div class="aset__actions">
            <button type="submit" class="sui-btn sui-btn--primary" :disabled="saving || !canSave">
              {{ saving ? '저장 중…' : '저장' }}
            </button>
          </div>
        </form>
      </div>
    </section>

    <section class="sui-card aset__preview" data-tour="admin-settings-preview">
      <div class="sui-card__head">
        <h2 class="sui-card__title">구매 기관에게 보이는 모습</h2>
      </div>
      <div class="sui-card__body">
        <dl class="aset__remote">
          <div>
            <dt>입금 은행</dt>
            <dd>{{ form.bankName || '—' }}</dd>
          </div>
          <div>
            <dt>계좌번호</dt>
            <dd>{{ form.account || '—' }}</dd>
          </div>
          <div>
            <dt>예금주</dt>
            <dd>{{ form.holder || '—' }}</dd>
          </div>
          <div v-if="form.notice">
            <dt>안내</dt>
            <dd>{{ form.notice }}</dd>
          </div>
          <div v-if="form.opsNotice">
            <dt>운영 공지</dt>
            <dd>{{ form.opsNotice }}</dd>
          </div>
        </dl>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { useAdminProducts } from '@/composables/useAdminProducts'
import { fetchPaymentInfo, saveAdminPaymentInfo, type MallPaymentInfo } from '@/api/mallOrder'

const { notify } = useAdminProducts()

const form = reactive<MallPaymentInfo>({
  bankName: '',
  account: '',
  holder: '',
  notice: '',
  opsNotice: '',
})

const loading = ref(false)
const saving = ref(false)
const errorMessage = ref('')

const canSave = computed(
  () => Boolean(form.bankName && form.account && form.holder) && !loading.value,
)

onMounted(async () => {
  loading.value = true
  errorMessage.value = ''
  try {
    const payment = await fetchPaymentInfo()
    form.bankName = payment.bankName
    form.account = payment.account
    form.holder = payment.holder
    form.notice = payment.notice
    form.opsNotice = payment.opsNotice || ''
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '입금 정보를 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
})

async function onSave() {
  if (!canSave.value || saving.value) return
  if (!window.confirm('입금 계좌 · 운영 설정을 저장할까요?')) return

  saving.value = true
  errorMessage.value = ''
  try {
    const payment = await saveAdminPaymentInfo({ ...form })
    form.bankName = payment.bankName
    form.account = payment.account
    form.holder = payment.holder
    form.notice = payment.notice
    form.opsNotice = payment.opsNotice || ''
    notify('설정을 저장했습니다.')
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '저장에 실패했습니다.'
  } finally {
    saving.value = false
  }
}
</script>

<style lang="scss" src="./AdminSettingsPage.scss"></style>
